import ExcelJS from 'exceljs'

/**
 * Adaptador responsável por converter a estrutura de exportação
 * utilizada pela aplicação para o formato esperado pelo ExcelJS.
 *
 * Mantém a compatibilidade com os campos e callbacks existentes.
 */
class ExcelJsAdapter {
  /**
   * Exporta registros para XLSX.
   *
   * @param {Object} options
   * @param {Array} options.data
   * @param {Object} options.fields
   * @param {string} options.name
   * @param {string} options.worksheet
   * @returns {Promise<void>}
   */
  async export({
    data = [],
    fields = {},
    name = 'file',
    worksheet = 'Planilha 1',
  } = {}) {
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet(worksheet)
    const entries = Object.entries(fields)

    sheet.columns = entries.map(([header]) => ({
      header,
      key: header,
      width: Math.max(15, header.length + 5),
    }))

    data.forEach((item) => {
      const row = {}

      entries.forEach(([header, config]) => {
        row[header] = this.normalizeValue(this.getValue(item, config))
      })

      sheet.addRow(row)
    })

    sheet.getRow(1).font = {
      bold: true,
    }

    sheet.getRow(1).alignment = {
      vertical: 'middle',
    }

    const buffer = await workbook.xlsx.writeBuffer()

    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    const filename = name.replace(/\.(xls|xlsx)$/i, '')

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = `${filename}.xlsx`

    try {
      document.body.appendChild(link)
      link.click()
    } finally {
      link.remove()

      setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 1000)
    }
  }

  /**
   * Obtém o valor de uma coluna, aplicando callbacks quando definidos.
   *
   * @param {Object} item
   * @param {string|Object} config
   * @returns {*}
   */
  getValue(item, config) {
    if (typeof config === 'string') {
      return this.getNestedValue(item, config)
    }

    if (!config || typeof config !== 'object') {
      return ''
    }

    const { field, callback, defaultValue = '' } = config

    let value = field ? this.getNestedValue(item, field) : item

    if (typeof callback === 'function') {
      value = callback(value)
    }

    return value == null ? defaultValue : value
  }

  /**
   * Obtém uma propriedade simples ou aninhada.
   *
   * @param {Object} item
   * @param {string} path
   * @returns {*}
   */
  getNestedValue(item, path) {
    const value = path.split('.').reduce((obj, key) => {
      return obj == null ? undefined : obj[key]
    }, item)

    return value == null ? '' : value
  }

  /**
   * Normaliza os valores para células do Excel.
   *
   * @param {*} value
   * @returns {*}
   */
  normalizeValue(value) {
    if (value == null) {
      return ''
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value instanceof Date
    ) {
      return value
    }

    return JSON.stringify(value)
  }
}

export default new ExcelJsAdapter()
